import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ─── Animated Space Canvas (shared with Login) ────────────────────────────────
function SpaceCanvas() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const S         = useRef({ stars: [], comets: [], aliens: [], satellites: [], ufo: null, tick: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const R = S.current;
    R.stars = Array.from({ length: 240 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.4 + 0.3,
      alpha: Math.random() * 0.6 + 0.3,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      twinklePhase: Math.random() * Math.PI * 2,
    }));
    const spawnComet = () => {
      const fromTop = Math.random() > 0.4;
      return {
        x: fromTop ? Math.random() * canvas.width : -60,
        y: fromTop ? -60 : Math.random() * canvas.height * 0.5,
        vx: 5 + Math.random() * 8, vy: fromTop ? 4 + Math.random() * 6 : 1.5 + Math.random() * 3,
        len: 90 + Math.random() * 130, alpha: 0, life: 0,
        maxLife: 160 + Math.random() * 120,
        color: ['#a5f3fc','#fde68a','#c4b5fd','#86efac'][Math.floor(Math.random() * 4)],
      };
    };
    R.comets = Array.from({ length: 3 }, () => { const c = spawnComet(); c.life = Math.floor(Math.random() * c.maxLife * 0.6); c.x += c.vx * c.life; c.y += c.vy * c.life; return c; });
    R.stars  = Array.from({ length: 240 }, () => ({ x: Math.random(), y: Math.random(), r: Math.random() * 1.4 + 0.3, alpha: Math.random() * 0.6 + 0.3, twinkleSpeed: Math.random() * 0.02 + 0.005, twinklePhase: Math.random() * Math.PI * 2 }));

    const loop = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      R.tick++;
      const bg = ctx.createRadialGradient(W * 0.5, H * 0.35, 0, W * 0.5, H * 0.5, W * 0.85);
      bg.addColorStop(0, '#0d0d2b'); bg.addColorStop(0.45, '#060614'); bg.addColorStop(1, '#020208');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      ctx.save(); ctx.globalAlpha = 0.055;
      const n1 = ctx.createRadialGradient(W*0.12,H*0.18,0,W*0.12,H*0.18,W*0.28);
      n1.addColorStop(0,'#7c3aed'); n1.addColorStop(1,'transparent');
      ctx.fillStyle=n1; ctx.fillRect(0,0,W,H);
      const n2 = ctx.createRadialGradient(W*0.88,H*0.72,0,W*0.88,H*0.72,W*0.32);
      n2.addColorStop(0,'#0e7490'); n2.addColorStop(1,'transparent');
      ctx.fillStyle=n2; ctx.fillRect(0,0,W,H);
      ctx.restore();
      R.stars.forEach(st => {
        const a = st.alpha + Math.sin(R.tick * st.twinkleSpeed + st.twinklePhase) * 0.28;
        ctx.beginPath(); ctx.arc(st.x*W, st.y*H, st.r, 0, Math.PI*2);
        ctx.fillStyle=`rgba(255,255,255,${Math.max(0,Math.min(1,a))})`; ctx.fill();
      });
      R.comets.forEach((c, i) => {
        c.life++; c.x+=c.vx; c.y+=c.vy;
        c.alpha = c.life<20?c.life/20:c.life>c.maxLife-20?(c.maxLife-c.life)/20:1;
        const mag=Math.hypot(c.vx,c.vy); const nx=c.vx/mag,ny=c.vy/mag;
        const tail=ctx.createLinearGradient(c.x,c.y,c.x-nx*c.len,c.y-ny*c.len);
        tail.addColorStop(0,`rgba(255,255,255,${c.alpha})`);
        tail.addColorStop(0.25,`${c.color}${Math.floor(c.alpha*180).toString(16).padStart(2,'0')}`);
        tail.addColorStop(1,'transparent');
        ctx.beginPath(); ctx.moveTo(c.x,c.y); ctx.lineTo(c.x-nx*c.len,c.y-ny*c.len);
        ctx.strokeStyle=tail; ctx.lineWidth=2; ctx.stroke();
        ctx.beginPath(); ctx.arc(c.x,c.y,2.5,0,Math.PI*2);
        ctx.fillStyle=`rgba(255,255,255,${c.alpha})`; ctx.fill();
        if(c.life>=c.maxLife||c.x>W+120||c.y>H+120) R.comets[i]=spawnComet();
      });
      if(R.tick%180===0&&R.comets.length<5) R.comets.push(spawnComet());
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position:'fixed', inset:0, width:'100%', height:'100%', zIndex:0, display:'block' }} />;
}

// ─── Reset Password Page ───────────────────────────────────────────────────────
export default function ResetPassword() {
  const [searchParams]       = useSearchParams();
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [status, setStatus]         = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg]     = useState('');
  const navigate                    = useNavigate();

  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!token) { setErrorMsg('Invalid reset link. Please request a new one.'); return; }
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErrorMsg('Passwords do not match.'); return; }

    setStatus('loading');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
      } else {
        setErrorMsg(data.message || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden' }}>
      <SpaceCanvas />
      <div style={{ position:'relative', zIndex:10, width:'100%', maxWidth:448, padding:'0 16px' }}>
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full">

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">ONLYnumber</h1>
            <p className="text-gray-600 mt-2">Set a new password</p>
          </div>

          {status === 'success' ? (
            <div className="text-center space-y-4">
              <div className="text-green-600 font-medium">Password updated successfully.</div>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
              >
                Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="••••••••"
                  required
                />
              </div>

              {(errorMsg || status === 'error') && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {status === 'loading' ? 'Updating...' : 'Set New Password'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
